/**
 * AB#20824 / AB#20825 — why an order's type is being changed.
 *
 * MIRRORS App\Enums\OrderTypeChangeReason. Every order-type change must carry one, so
 * that "why was this order flagged Remake?" has an answer six months later.
 *
 * NOTE these are NOT the failure categories from the Remake Intake Questionnaire. They
 * answer different questions: this list is why the CLASSIFICATION changed (it was entered
 * wrongly, new information arrived); the failure categories describe what went wrong with
 * the GARMENT (sizing, print, stitching). AB#20569 treats them as one thing; they are not,
 * and the audit trail needs both to tell a data-entry correction from a real failure.
 * See AB#20945.
 */
export interface OrderTypeChangeReason {
  code: string;
  label: string;
  /** Order-type symbols this reason may accompany, or '*' for any. */
  appliesTo: string[] | '*';
  /** When true the free-text comment is mandatory. */
  requiresComment: boolean;
}

export const orderTypeChangeReasons: OrderTypeChangeReason[] = [
  {
    code: 'wrongly_classified_at_entry',
    label: 'Wrongly classified at entry',
    appliesTo: '*',
    requiresComment: false,
  },
  {
    code: 'new_information',
    label: 'New information',
    appliesTo: '*',
    requiresComment: false,
  },
  {
    // The order is being classified as a failure for the first time because something
    // actually went wrong. Directional: it explains moving an order INTO a failure type,
    // the mirror of 'customer_confirmed_no_fault' below.
    //
    // Without it, filing a remake meant picking "New information" or "Other" and writing
    // twenty characters to explain a change the questionnaire underneath already explains
    // in full — a required field whose only truthful answer was missing.
    code: 'failure_identified',
    label: 'Failure identified',
    appliesTo: ['Ri', 'R'],
    requiresComment: false,
  },
  {
    // Directional: this explains moving an order OUT of a failure type, so it is not
    // offered when changing INTO a remake.
    code: 'customer_confirmed_no_fault',
    label: 'Customer confirmed no fault',
    appliesTo: ['REORDER'],
    requiresComment: false,
  },
  {
    code: 'duplicate_record',
    label: 'Duplicate record',
    appliesTo: '*',
    requiresComment: false,
  },
  {
    // Carries no information on its own, so it has to be explained.
    code: 'other',
    label: 'Other',
    appliesTo: '*',
    requiresComment: true,
  },
];

/** The minimum a comment must say to be worth storing. Matches the questionnaire specification. */
export const ORDER_TYPE_CHANGE_COMMENT_MIN_LENGTH = 20;

export function reasonsForOrderType(symbol?: string | null): OrderTypeChangeReason[] {
  if (!symbol) return orderTypeChangeReasons;
  const trimmed = symbol.trim();

  return orderTypeChangeReasons.filter(
    (reason) => reason.appliesTo === '*' || reason.appliesTo.includes(trimmed),
  );
}

export function findOrderTypeChangeReason(code?: string | null): OrderTypeChangeReason | undefined {
  if (!code) return undefined;
  return orderTypeChangeReasons.find((reason) => reason.code === code);
}
